import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { ToggleSwitch } from '@/shared/components/custom/toggle-switch/ToggleSwitch';
import { useCreateHoliday } from '../../hooks/useCreateHoliday';
import { useUpdateHoliday } from '../../hooks/useUpdateHoliday';
import { holidaySchema, type HolidayFormValues } from '../../schemas';
import type { HolidayDto } from '../../types';

interface HolidayFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingHoliday?: HolidayDto | null;
}

const EMPTY_VALUES: HolidayFormValues = { date: '', name: '', isOfficial: false };

export function HolidayFormDialog({ open, onOpenChange, editingHoliday }: HolidayFormDialogProps) {
  const { t } = useTranslation('timeOff');
  const createHoliday = useCreateHoliday();
  const updateHoliday = useUpdateHoliday();

  const { control, handleSubmit, reset } = useForm<HolidayFormValues>({
    resolver: zodResolver(holidaySchema(t)),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (open) {
      reset(
        editingHoliday
          ? {
              date: editingHoliday.date,
              name: editingHoliday.name ?? '',
              isOfficial: editingHoliday.isOfficial,
            }
          : EMPTY_VALUES,
      );
    }
  }, [open, editingHoliday, reset]);

  function onSubmit(values: HolidayFormValues) {
    if (editingHoliday?.id) {
      updateHoliday.mutate(
        { holidayId: editingHoliday.id, payload: values },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      createHoliday.mutate(values, { onSuccess: () => onOpenChange(false) });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="time-off-settings-holiday-dialog">
        <DialogHeader>
          <DialogTitle>
            {editingHoliday ? t('settings.holidays.editTitle') : t('settings.holidays.createTitle')}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
          data-testid="time-off-settings-holiday-form"
        >
          <FormFieldInput
            control={control}
            name="date"
            type="date"
            label={t('settings.holidays.date')}
            data-testid="time-off-settings-holiday-date"
          />
          <FormFieldInput
            control={control}
            name="name"
            label={t('settings.types.columns.name')}
            data-testid="time-off-settings-holiday-name"
          />
          <Controller
            name="isOfficial"
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                id="holiday-is-official"
                checked={field.value}
                onCheckedChange={field.onChange}
                label={t('settings.holidays.official')}
                data-testid="time-off-settings-holiday-official"
              />
            )}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('settings.holidays.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={createHoliday.isPending || updateHoliday.isPending}
              data-testid="time-off-settings-holiday-submit"
            >
              {editingHoliday ? t('settings.holidays.save') : t('settings.holidays.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
